import { InfoCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Table, Tag } from "antd";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import AgentSchedulesModal from "./AgentSchedulesModal";

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1));

/**
 * Notifikasi "sistem otomatis memilih {agent}" + modal alasan (priority_metadata
 * dari endpoint availability/agent dengan include_metadata=true).
 */
const AutoAgentNotice = ({ agentName, reasoning }) => {
  const { t, i18n } = useTranslation();
  const numLocale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";
  const formatNumber = (v) => Number(v ?? 0).toLocaleString(numLocale);
  const formatWorkHours = (m) => {
    const total = Number(m ?? 0);
    const hStr = Math.floor(total / 60).toLocaleString(numLocale);
    const min = total % 60;
    // Jika menit 0, tampilkan jam saja.
    if (min === 0) {
      return t("meeting_schedule.auto_workhours_h", { h: hStr });
    }
    return t("meeting_schedule.auto_workhours", { h: hStr, m: min });
  };
  const [open, setOpen] = useState(false);
  const [agentModal, setAgentModal] = useState(null);

  if (!agentName) return null;

  const candidates = Array.isArray(reasoning?.candidates)
    ? reasoning.candidates
    : [];
  const dataSource = candidates
    .map((c, i) => ({
      ...c,
      key: c.schedule_admin_id ?? i,
      is_picked: c.agent_name === agentName,
    }))
    .sort((a, b) => {
      // Urut berdasarkan prioritas (pick_order): winner dulu, yang tak tersedia (null) di akhir.
      const ao = a.pick_order == null ? Infinity : a.pick_order;
      const bo = b.pick_order == null ? Infinity : b.pick_order;
      return ao - bo;
    });

  // Buka daftar jadwal agent (rentang default modal: hari ini −30 hari … +14 hari).
  const openAgentSchedules = (r) =>
    setAgentModal({
      scheduleAdminId: r.schedule_admin_id,
      agentName: r.agent_name,
    });

  const columns = [
    {
      title: t("meeting_schedule.auto_col_agent"),
      dataIndex: "agent_name",
      key: "agent_name",
      render: (v, r) => (
        <Button
          type="link"
          size="small"
          className={`!h-auto !px-0 ${
            r.is_picked ? "!font-semibold !text-emerald-600" : ""
          }`}
          onClick={() => openAgentSchedules(r)}
        >
          {v}
          {r.is_picked ? ` (${t("meeting_schedule.auto_picked")})` : ""}
        </Button>
      ),
    },
    {
      title: t("meeting_schedule.auto_col_priority"),
      dataIndex: "pick_order",
      key: "pick_order",
      align: "center",
      render: (v) => (v == null ? "—" : v),
    },
    {
      title: t("meeting_schedule.auto_col_workhours"),
      dataIndex: "work_minutes",
      key: "work_minutes",
      align: "right",
      render: (v) => formatWorkHours(v),
    },
    {
      title: t("meeting_schedule.auto_col_quota"),
      dataIndex: "quota",
      key: "quota",
      align: "right",
      render: fmt,
    },
    {
      title: t("meeting_schedule.auto_col_actual"),
      dataIndex: "actual",
      key: "actual",
      align: "right",
      render: (v, r) => (
        <Button
          type="link"
          size="small"
          className="!px-0"
          onClick={() => openAgentSchedules(r)}
        >
          {v ?? 0}
        </Button>
      ),
    },
    {
      title: t("meeting_schedule.auto_col_deficit"),
      dataIndex: "deficit",
      key: "deficit",
      align: "right",
      render: (v) => {
        const n = Number(v ?? 0);
        const color =
          n < 0 ? "text-red-500" : n > 0 ? "text-emerald-600" : "";
        return <span className={`font-medium ${color}`}>{fmt(v)}</span>;
      },
    },
    {
      title: t("meeting_schedule.auto_col_available"),
      dataIndex: "available_at_slot",
      key: "available_at_slot",
      align: "center",
      render: (v) =>
        v ? (
          <Tag color="green" className="m-0">
            ✓
          </Tag>
        ) : (
          <Tag className="m-0">—</Tag>
        ),
    },
  ];

  return (
    <>
      <Alert
        type="success"
        showIcon
        className="my-3"
        message={
          <Trans
            i18nKey="meeting_schedule.auto_agent_selected"
            values={{ name: agentName }}
            components={{ bold: <strong /> }}
          />
        }
        action={
          reasoning ? (
            <Button
              type="link"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() => setOpen(true)}
            >
              {t("meeting_schedule.auto_view_reason")}
            </Button>
          ) : null
        }
      />
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={960}
        title={t("meeting_schedule.auto_reason_title")}
        destroyOnHidden
      >
        <p className="mb-3 text-sm text-neutral-600">
          {t("meeting_schedule.auto_reason_desc")}
        </p>
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowClassName={(r) =>
            r.is_picked ? "[&>td]:!bg-emerald-50" : ""
          }
        />
        <div className="mt-3 space-y-1 rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600">
          <p className="mb-0">
            <b>{t("meeting_schedule.auto_col_quota")}</b>:{" "}
            {t("meeting_schedule.auto_legend_quota")}
          </p>
          <p className="mb-0">
            <b>{t("meeting_schedule.auto_col_actual")}</b>:{" "}
            {t("meeting_schedule.auto_legend_actual")}
          </p>
          <p className="mb-0">
            <b>{t("meeting_schedule.auto_col_deficit")}</b>:{" "}
            {t("meeting_schedule.auto_legend_deficit")}
          </p>
          <p className="mb-0">
            <b>{t("meeting_schedule.auto_col_available")}</b>:{" "}
            {t("meeting_schedule.auto_legend_available")}
          </p>
        </div>
        {(reasoning?.total_meetings != null ||
          reasoning?.total_work_minutes != null) && (
          <div className="mt-3 text-xs text-neutral-500">
            {t("meeting_schedule.auto_total_meetings")}:{" "}
            {formatNumber(reasoning?.total_meetings)} ·{" "}
            {t("meeting_schedule.auto_total_work")}:{" "}
            {formatWorkHours(reasoning?.total_work_minutes)}
          </div>
        )}
      </Modal>
      <AgentSchedulesModal
        open={Boolean(agentModal)}
        onClose={() => setAgentModal(null)}
        scheduleAdminId={agentModal?.scheduleAdminId}
        agentName={agentModal?.agentName}
      />
    </>
  );
};

export default AutoAgentNotice;
